export type PermissionScope = "global" | "branch" | "own" | "assigned";

export const SCOPE_ORDER: PermissionScope[] = [
  "own",
  "assigned",
  "branch",
  "global",
];

export const SCOPE_LABEL: Record<PermissionScope, string> = {
  own: "Solo información propia",
  assigned: "Solo registros asignados",
  branch: "Solo su sucursal",
  global: "Todas las sucursales",
};

export const SCOPE_RESTRICTION: Record<PermissionScope, string> = {
  own: "Podrá actuar solo sobre su propia información, no sobre la de otras personas.",
  assigned:
    "Podrá actuar solo sobre los registros que tenga asignados, no sobre el resto.",
  branch:
    "Podrá actuar sobre los registros de su sucursal, pero no sobre los de otras sucursales.",
  global: "Podrá actuar en todas las sucursales.",
};

export const MODULE_LABEL: Record<string, string> = {
  users: "Cuentas",
  roles: "Roles",
  partners: "Colaboradores",
  applications: "Solicitudes",
  calendars: "Calendario líder",
  vacancies: "Vacantes",
  positions: "Puestos",
  contracts: "Contratos",
  proceedings: "Expedientes",
  files: "Archivos",
  payroll: "Nómina",
  labor_permissions: "Permisos laborales",
  holidays: "Vacaciones",
  incapacities: "Incapacidades",
  incidents: "Incidencias",
  attendance: "Asistencia",
  loans: "Préstamos",
  missing_box: "Faltantes de caja",
  complaints: "Quejas",
  blacklists: "Lista negra",
  branches: "Sucursales",
  configuration: "Configuración",
  catalogs: "Catálogos",
  emails: "Correo",
  deductions: "Deducciones",
  perceptions: "Percepciones",
  reports: "Reportes",
  notifications: "Notificaciones",
  tactical: "Táctica operativa",
  direct_boss: "Jefes directos",
};

const ALL: PermissionScope[] = ["global", "branch", "own", "assigned"];
const GLOBAL_ONLY: PermissionScope[] = ["global"];

type Copy = {
  name: string;
  description: string;
  scopes: PermissionScope[];
  sensitive?: boolean;
};

const entry = (
  name: string,
  description: string,
  scopes: PermissionScope[] = ALL,
  sensitive = false,
): Copy => ({ name, description, scopes, sensitive });

export const PERMISSION_COPY: Record<string, Copy> = {
  "users.view": entry("Ver cuentas", "Permite consultar las cuentas de acceso al sistema.", GLOBAL_ONLY),
  "users.create": entry("Crear cuentas", "Permite dar de alta cuentas de acceso.", GLOBAL_ONLY),
  "users.edit": entry("Editar cuentas", "Permite modificar datos de las cuentas de acceso.", GLOBAL_ONLY),
  "users.delete": entry("Eliminar cuentas", "Permite eliminar cuentas de acceso.", GLOBAL_ONLY, true),
  "users.assign_role": entry("Asignar rol a una cuenta", "Permite cambiar el rol de una cuenta.", GLOBAL_ONLY, true),
  "roles.view": entry("Ver roles", "Permite consultar los roles y sus permisos.", GLOBAL_ONLY),
  "roles.assign_permissions": entry("Administrar permisos del rol", "Permite cambiar qué puede hacer un rol.", GLOBAL_ONLY, true),
  "roles.create": entry("Crear roles", "Permite crear roles personalizados.", GLOBAL_ONLY),
  "roles.edit": entry("Editar roles", "Permite cambiar el nombre o los datos de un rol personalizado.", GLOBAL_ONLY),
  "roles.deactivate": entry("Activar o desactivar roles", "Permite activar o desactivar un rol personalizado.", GLOBAL_ONLY),
  "roles.delete": entry("Eliminar roles", "Permite eliminar un rol personalizado que no esté en uso.", GLOBAL_ONLY, true),
  "partners.view": entry("Ver colaboradores", "Permite consultar la información de los colaboradores."),
  "partners.edit": entry("Editar colaboradores", "Permite modificar la información de los colaboradores, sin incluir el salario."),
  "partners.unsubscribe": entry("Dar de baja colaboradores", "Permite dar de baja, reactivar o suspender colaboradores.", ALL, true),
  "partners.import": entry("Importar colaboradores", "Permite cargar colaboradores desde un archivo."),
  "partners.view_salary": entry("Ver salario", "Permite consultar el salario contractual.", ALL, true),
  "partners.edit_salary": entry("Editar salario", "Permite modificar el salario contractual.", ALL, true),
  "partners.view_imss_salary": entry("Ver salario IMSS", "Permite consultar el salario registrado ante el IMSS.", ALL, true),
  "partners.edit_imss_salary": entry("Editar salario IMSS", "Permite modificar el salario registrado ante el IMSS.", ALL, true),
  "applications.view": entry("Ver solicitudes", "Permite consultar las solicitudes de ingreso."),
  "calendars.leader": entry(
    "Calendario del líder",
    "Permite consultar y administrar el calendario propio del líder, sin abrir las solicitudes de ingreso.",
    ["own", "global"],
  ),
  "applications.review": entry("Revisar solicitudes", "Permite avanzar, aprobar o rechazar una solicitud."),
  "applications.create_internal": entry("Alta interna", "Permite registrar un postulante desde la operación."),
  "vacancies.view": entry("Ver vacantes", "Permite consultar las vacantes."),
  "vacancies.manage": entry("Administrar vacantes", "Permite crear, editar o desactivar vacantes."),
  "positions.view": entry("Ver puestos", "Permite consultar los puestos."),
  "positions.manage": entry("Administrar puestos", "Permite crear o modificar puestos."),
  "contracts.view": entry("Ver contratos", "Permite consultar contratos."),
  "contracts.manage": entry("Administrar contratos", "Permite administrar plantillas y contratos."),
  "proceedings.view": entry("Ver expedientes", "Permite consultar el expediente de un colaborador."),
  "proceedings.review": entry("Revisar documentos", "Permite aprobar o rechazar documentos del expediente."),
  "files.view": entry("Consultar archivos", "Permite ver y descargar archivos."),
  "files.upload": entry("Subir archivos", "Permite cargar archivos."),
  "files.delete": entry("Eliminar archivos", "Permite borrar archivos.", ALL, true),
  "payroll.view": entry("Ver nómina", "Permite consultar la nómina."),
  "payroll.calculate": entry("Calcular nómina", "Permite calcular una corrida de nómina.", ALL, true),
  "payroll.edit": entry("Editar nómina", "Permite modificar desgloses y ajustes de nómina.", ALL, true),
  "labor_permissions.view": entry("Ver permisos laborales", "Permite consultar los permisos laborales."),
  "labor_permissions.request": entry("Solicitar permisos laborales", "Permite registrar una solicitud de permiso."),
  "labor_permissions.approve": entry("Aprobar permisos laborales", "Permite aprobar un permiso laboral."),
  "labor_permissions.reject": entry("Rechazar permisos laborales", "Permite rechazar un permiso laboral."),
  "holidays.view": entry("Ver vacaciones", "Permite consultar las vacaciones."),
  "holidays.request": entry("Solicitar vacaciones", "Permite registrar una solicitud de vacaciones."),
  "holidays.approve": entry("Aprobar vacaciones", "Permite aprobar vacaciones."),
  "incapacities.view": entry("Ver incapacidades", "Permite consultar incapacidades."),
  "incapacities.request": entry("Registrar incapacidades", "Permite cargar una incapacidad."),
  "incapacities.approve": entry("Aprobar incapacidades", "Permite aprobar una incapacidad."),
  "incidents.view": entry("Ver incidencias", "Permite consultar incidencias de asistencia."),
  "incidents.manage": entry("Resolver incidencias", "Permite capturar o resolver incidencias."),
  "incidents.release": entry(
    "Liberar incidencias del equipo",
    "Permite a un jefe directo aceptar o rechazar las incidencias de los colaboradores que tiene asignados.",
    ALL,
    true,
  ),
  "attendance.view": entry("Consultar asistencia", "Permite consultar los registros de asistencia."),
  "attendance.check": entry("Registrar asistencia", "Permite registrar la entrada o la salida."),
  "attendance.configure_schedule": entry("Configurar horarios", "Permite definir los horarios de la sucursal."),
  "loans.view": entry("Ver préstamos", "Permite consultar préstamos."),
  "loans.manage": entry("Administrar préstamos", "Permite crear o modificar préstamos."),
  "missing_box.view": entry("Ver faltantes de caja", "Permite consultar faltantes de caja."),
  "missing_box.manage": entry("Administrar faltantes de caja", "Permite registrar o resolver faltantes de caja."),
  "complaints.view": entry("Ver quejas", "Permite consultar quejas."),
  "complaints.manage": entry("Administrar quejas", "Permite registrar o dar seguimiento a quejas."),
  "blacklists.view": entry("Ver lista negra", "Permite consultar la lista negra."),
  "blacklists.manage": entry("Administrar lista negra", "Permite agregar o modificar registros de la lista negra.", ALL, true),
  "branches.view": entry("Ver sucursales", "Permite consultar sucursales."),
  "branches.create": entry("Crear sucursales", "Permite dar de alta sucursales.", GLOBAL_ONLY),
  "branches.edit": entry("Editar sucursales", "Permite modificar los datos de una sucursal."),
  "configuration.view": entry("Ver configuración", "Permite consultar la configuración del sistema.", GLOBAL_ONLY),
  "configuration.edit": entry("Editar configuración", "Permite cambiar la configuración del sistema.", GLOBAL_ONLY, true),
  "catalogs.view": entry("Ver catálogos", "Permite consultar catálogos de referencia.", GLOBAL_ONLY),
  "catalogs.manage": entry("Administrar catálogos", "Permite modificar catálogos de referencia.", GLOBAL_ONLY),
  "emails.view": entry("Ver correo", "Permite consultar la configuración de correo.", GLOBAL_ONLY),
  "emails.manage": entry("Administrar correo", "Permite cambiar el envío y las plantillas de correo.", GLOBAL_ONLY),
  "deductions.view": entry("Ver deducciones", "Permite consultar deducciones."),
  "deductions.edit": entry("Editar deducciones", "Permite capturar o modificar deducciones."),
  "perceptions.view": entry("Ver percepciones", "Permite consultar bonos y percepciones."),
  "perceptions.edit": entry("Editar percepciones", "Permite capturar o modificar bonos y percepciones."),
  "reports.view": entry("Ver reportes", "Permite consultar reportes de la operación.", GLOBAL_ONLY),
  "reports.birthdates": entry("Ver cumpleaños", "Permite consultar los cumpleaños de los colaboradores."),
  "notifications.manage": entry("Administrar notificaciones", "Permite enviar o configurar notificaciones masivas.", GLOBAL_ONLY),
  "tactical.view": entry("Ver táctica operativa", "Permite consultar la táctica operativa."),
  "direct_boss.manage": entry("Administrar jefes directos", "Permite asignar qué personas reportan a un jefe."),
};

export function scopeLabel(scope: string): string {
  return SCOPE_LABEL[scope as PermissionScope] || "";
}

export function moduleLabel(moduleName: string): string {
  return MODULE_LABEL[moduleName] || moduleName;
}

export function permissionCopy(code: string): Copy {
  return (
    PERMISSION_COPY[code] || {
      name: code,
      description: "Permiso del sistema.",
      scopes: ALL,
    }
  );
}

export function scopeChoices(code: string, current?: string): PermissionScope[] {
  const allowed = permissionCopy(code).scopes;
  if (current && !allowed.includes(current as PermissionScope)) {
    return [current as PermissionScope, ...allowed];
  }
  return allowed;
}

export function scopeIsAllowed(code: string, scope: string): boolean {
  return permissionCopy(code).scopes.includes(scope as PermissionScope);
}
